import React, { useState, useEffect, useRef } from 'react';
import { Plus, Package, BookOpen, Loader2, X, Camera, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import ImageCropDialog from '@/components/ImageCropDialog';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { recipesAPI } from '@/lib/api';
import { PhoneCaptureButton } from '@/components/PhoneCapture';
import { formatCurrency, pluralUnit, parseContenanceFromName } from './recipeUtils';
import IngredientRow from './IngredientRow';

export default function RecipeFormDialog({ open, onClose, recipe, onSaved }) {
    const [formData, setFormData] = useState({
        name: '', description: '', category: '', portions: 1, ingredients: []
    });
    const [saving, setSaving] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imageDeleted, setImageDeleted] = useState(false);
    const [imageHeight, setImageHeight] = useState('');
    const [cropSrc, setCropSrc] = useState(null);
    const [showCrop, setShowCrop] = useState(false);
    const [copySourceId, setCopySourceId] = useState(null);
    const [showPicker, setShowPicker] = useState(false);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    const handleFileSelected = (file) => {
        if (file) {
            const url = URL.createObjectURL(file);
            setCropSrc(url);
            setShowCrop(true);
        }
    };

    const handleCropped = (croppedFile, previewUrl) => {
        setImageFile(croppedFile);
        setImagePreview(previewUrl);
        setImageDeleted(false);
        setCopySourceId(null);
        setCropSrc(null);
    };

    useEffect(() => {
        if (open) {
            setImageFile(null);
            setImageDeleted(false);
            setCopySourceId(null);
            if (recipe) {
                setImagePreview(recipe.image_url ? `${process.env.REACT_APP_BACKEND_URL}${recipe.image_url}` : null);
                setImageHeight(recipe.image_height ? String(recipe.image_height) : '');
                setFormData({
                    name: recipe.name || '',
                    description: recipe.description || '',
                    category: recipe.category || '',
                    portions: recipe.portions || 1,
                    yield_unit: recipe.yield_unit || 'portion',
                    is_sub_recipe: recipe.is_sub_recipe || false,
                    fuji_express: recipe.fuji_express !== false,
                    concept_lab: recipe.concept_lab !== false,
                    my_food: recipe.my_food !== false,
                    boucherie: recipe.boucherie !== false,
                    ingredients: (recipe.ingredients || []).map(ing => {
                        const useBase = ing.use_base_unit || false;
                        const baseUnit = ing.base_unit || '';
                        const purchaseUnit = ing.purchase_unit || ing.unit || '';
                        let contenance = ing.contenance || null;
                        
                        if (useBase && !contenance && ing.supplier_product_name) {
                            contenance = parseContenanceFromName(ing.supplier_product_name, baseUnit) || null;
                        }
                        
                        const rawPrice = ing.purchase_unit_price || (
                            useBase && contenance > 0 && ing.unit_cost > 0
                                ? ing.unit_cost * contenance
                                : ing.unit_cost || 0
                        );
                        
                        let unitCost = ing.unit_cost || 0;
                        if (useBase && contenance > 0 && rawPrice > 0) {
                            unitCost = rawPrice / contenance;
                        }
                        
                        return {
                            ...ing,
                            use_base_unit: useBase,
                            contenance,
                            base_unit: baseUnit,
                            purchase_unit: purchaseUnit,
                            unit_cost: Math.round(unitCost * 10000) / 10000,
                            total_cost: Math.round((ing.quantity || 0) * unitCost * 10000) / 10000,
                            _raw_unit_price: rawPrice,
                            _supplierProducts: [],
                        };
                    }),
                });
            } else {
                setImagePreview(null);
                setImageHeight('');
                setFormData({ name: '', description: '', category: '', portions: 1, yield_unit: 'portion', is_sub_recipe: false, ingredients: [], fuji_express: true, concept_lab: true, my_food: true, boucherie: true });
            }
        }
    }, [open, recipe]);

    const addIngredient = () => {
        setFormData(prev => ({
            ...prev,
            ingredients: [...prev.ingredients, {
                type: 'product',
                industrial_product_id: '',
                supplier_product_id: '',
                sub_recipe_id: '',
                quantity: 0,
                unit: '',
                unit_cost: 0,
                total_cost: 0,
                industrial_product_name: '',
                supplier_product_name: '',
                sub_recipe_name: '',
                use_base_unit: false,
                contenance: null,
                base_unit: '',
                purchase_unit: '',
                _raw_unit_price: 0,
                _supplierProducts: [],
            }],
        }));
    };

    const updateIngredient = (index, data) => {
        setFormData(prev => {
            const ingredients = [...prev.ingredients];
            ingredients[index] = data;
            return { ...prev, ingredients };
        });
    };

    const removeIngredient = (index) => {
        setFormData(prev => ({
            ...prev,
            ingredients: prev.ingredients.filter((_, i) => i !== index),
        }));
    };

    const moveIngredient = (fromIndex, toIndex) => {
        setFormData(prev => {
            const newIngredients = [...prev.ingredients];
            const [moved] = newIngredients.splice(fromIndex, 1);
            newIngredients.splice(toIndex, 0, moved);
            return { ...prev, ingredients: newIngredients };
        });
    };

    const totalCost = formData.ingredients.reduce((sum, ing) => sum + (ing.total_cost || 0), 0);
    const costPerPortion = formData.portions > 0 ? totalCost / formData.portions : 0;
    const yieldLabel = formData.yield_unit || 'portion';

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Le nom de la recette est requis');
            return;
        }

        setSaving(true);
        try {
            const cleanIngredients = formData.ingredients
                .filter(ing => ing.industrial_product_id || ing.sub_recipe_id)
                .map(ing => ({
                    type: ing.type,
                    industrial_product_id: ing.industrial_product_id || null,
                    supplier_product_id: ing.supplier_product_id || null,
                    sub_recipe_id: ing.sub_recipe_id || null,
                    quantity: ing.quantity || 0,
                    unit: ing.unit || '',
                    use_base_unit: ing.use_base_unit || false,
                    contenance: ing.contenance || null,
                }));

            const payload = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                category: formData.category.trim(),
                portions: formData.portions || 1,
                yield_unit: formData.yield_unit || 'portion',
                is_sub_recipe: formData.is_sub_recipe || false,
                fuji_express: formData.fuji_express !== false,
                concept_lab: formData.concept_lab !== false,
                my_food: formData.my_food !== false,
                boucherie: formData.boucherie !== false,
                image_height: imageHeight ? parseInt(imageHeight, 10) : null,
                ingredients: cleanIngredients,
            };

            let recipeId = recipe?.id;
            if (recipeId) {
                await recipesAPI.update(recipeId, payload);
                toast.success('Recette mise à jour');
            } else {
                const res = await recipesAPI.create(payload);
                recipeId = res.data.id;
                toast.success('Recette créée');
            }

            if (imageFile && recipeId) {
                await recipesAPI.uploadImage(recipeId, imageFile);
            } else if (copySourceId && recipeId) {
                await recipesAPI.copyImage(recipeId, copySourceId);
            } else if (imageDeleted && recipeId) {
                await recipesAPI.deleteImage(recipeId);
            }

            onSaved();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[90rem] sm:w-[96vw] max-h-[92vh] overflow-y-auto" data-testid="recipe-form-dialog">
                <DialogHeader className="pb-0">
                    <DialogTitle className="text-base" data-testid="recipe-form-title">
                        {recipe?.id ? 'Modifier la recette' : 'Nouvelle recette'}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        {recipe?.id ? 'Modifiez les informations de la recette.' : 'Remplissez les informations pour créer une nouvelle recette.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-1">
                    {/* Basic Info (compact single row) */}
                    <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
                        <div className="col-span-2 md:col-span-4">
                            <Label htmlFor="recipe-name" className="text-xs">Nom de la recette *</Label>
                            <Input
                                id="recipe-name"
                                className="h-8 text-sm"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Ex: Sauce tomate maison"
                                data-testid="recipe-name-input"
                            />
                        </div>
                        <div className="md:col-span-3">
                            <Label htmlFor="recipe-category" className="text-xs">Catégorie</Label>
                            <Input
                                id="recipe-category"
                                className="h-8 text-sm"
                                value={formData.category}
                                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                placeholder="Ex: Sauces, Plats..."
                                data-testid="recipe-category-input"
                            />
                        </div>
                        <div className="md:col-span-3">
                            <Label htmlFor="recipe-description" className="text-xs">Description</Label>
                            <Input
                                id="recipe-description"
                                className="h-8 text-sm"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Optionnelle"
                                data-testid="recipe-description-input"
                            />
                        </div>
                        <div className="md:col-span-1">
                            <Label htmlFor="recipe-portions" className="text-xs">Rendement</Label>
                            <Input
                                id="recipe-portions"
                                className="h-8 text-sm"
                                type="number"
                                step="any"
                                min="0.01"
                                value={formData.portions}
                                onChange={(e) => setFormData(prev => ({ ...prev, portions: parseFloat(e.target.value) || 1 }))}
                                data-testid="recipe-portions-input"
                            />
                        </div>
                        <div className="md:col-span-1">
                            <Label htmlFor="recipe-yield-unit" className="text-xs">Unité</Label>
                            <Select
                                value={formData.yield_unit}
                                onValueChange={(v) => setFormData(prev => ({ ...prev, yield_unit: v }))}
                            >
                                <SelectTrigger className="h-8 text-sm" data-testid="recipe-yield-unit-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="portion">portion</SelectItem>
                                    <SelectItem value="kg">kg</SelectItem>
                                    <SelectItem value="L">litre</SelectItem>
                                    <SelectItem value="pièce">pièce</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Sous-recette + Entreprises (compact single row) */}
                    <div className="flex flex-wrap items-center gap-2">
                        <label
                            className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-violet-200 bg-violet-50 hover:bg-violet-100 cursor-pointer text-xs"
                        >
                            <Checkbox
                                id="recipe-is-sub"
                                checked={formData.is_sub_recipe}
                                onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_sub_recipe: !!v }))}
                                data-testid="recipe-is-sub-checkbox"
                            />
                            <span>Sous-recette</span>
                        </label>
                        <span className="text-xs text-slate-400 ml-2">Utilisable par :</span>
                        {[
                            { key: 'my_food', label: 'My Food' },
                            { key: 'fuji_express', label: 'Fuji Express' },
                            { key: 'concept_lab', label: 'Concept Lab' },
                            { key: 'boucherie', label: 'Boucherie' },
                        ].map(({ key, label }) => (
                            <label key={key}
                                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-xs"
                                data-testid={`recipe-company-${key}`}>
                                <input type="checkbox" checked={formData[key] !== false}
                                    onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.checked }))}
                                    className="rounded" />
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>

                    {/* Photo (compact horizontal) */}
                    <div className="flex items-center gap-3">
                        {imagePreview ? (
                            <div className="relative w-20 h-16 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0">
                                <img src={imagePreview} alt="Recette" className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => { setImagePreview(null); setImageFile(null); setCopySourceId(null); setImageDeleted(true); }}
                                    className="absolute top-0.5 right-0.5 bg-black/50 rounded-full p-0.5 text-white hover:bg-black/70"
                                    data-testid="recipe-remove-image"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ) : (
                            <div className="w-20 h-16 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50 flex-shrink-0">
                                <ImageIcon className="w-6 h-6 text-slate-300" />
                            </div>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileSelected(file);
                                e.target.value = '';
                            }}
                            data-testid="recipe-image-file-input"
                        />
                        <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => fileInputRef.current?.click()}>
                            <ImageIcon className="w-3.5 h-3.5 mr-1" /> Choisir un fichier
                        </Button>
                        <input
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileSelected(file);
                                e.target.value = '';
                            }}
                        />
                        <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => cameraInputRef.current?.click()}>
                            <Camera className="w-3.5 h-3.5 mr-1" /> Prendre une photo
                        </Button>
                        <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowPicker(true)} data-testid="recipe-image-existing-btn">
                            <BookOpen className="w-3.5 h-3.5 mr-1" /> Choisir existante
                        </Button>
                        <PhoneCaptureButton
                            size="sm"
                            className="h-8 text-xs"
                            label="Téléphone"
                            multiple={false}
                            testId="recipe-image-phone-btn"
                            onPhotos={(fs) => fs[0] && handleFileSelected(fs[0])}
                        />
                        <div className="flex items-center gap-1.5">
                            <label className="text-xs text-slate-500 whitespace-nowrap">Hauteur :</label>
                            <input
                                type="number"
                                placeholder="Auto"
                                value={imageHeight}
                                onChange={(e) => setImageHeight(e.target.value)}
                                className="w-16 text-xs border border-slate-200 rounded px-1.5 py-1 text-center"
                                min={50}
                                data-testid="recipe-image-height-input"
                            />
                            <span className="text-[10px] text-slate-400">px</span>
                        </div>
                        <ImageCropDialog
                            open={showCrop}
                            onClose={() => { setShowCrop(false); setCropSrc(null); }}
                            imageSrc={cropSrc}
                            onCropped={(croppedFile, previewUrl, usedHeight) => {
                                handleCropped(croppedFile, previewUrl);
                                if (usedHeight) setImageHeight(String(usedHeight));
                            }}
                            defaultMaxHeight={imageHeight ? parseInt(imageHeight, 10) : null}
                        />
                        <ExistingImagePicker
                            open={showPicker}
                            onClose={() => setShowPicker(false)}
                            excludeId={recipe?.id}
                            onSelect={(r) => {
                                setCopySourceId(r.id);
                                setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${r.image_url}`);
                                setImageFile(null);
                                setImageDeleted(false);
                                setShowPicker(false);
                            }}
                        />
                    </div>

                    {/* Ingredients Section */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-slate-900">Ingrédients</h3>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={addIngredient}
                                data-testid="add-ingredient-btn"
                            >
                                <Plus className="w-4 h-4 mr-1" /> Ajouter
                            </Button>
                        </div>

                        {formData.ingredients.length === 0 ? (
                            <div className="text-center py-8 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Aucun ingrédient ajouté</p>
                                <p className="text-xs mt-1">Cliquez sur "Ajouter" pour commencer</p>
                            </div>
                        ) : (
                            <div className="border border-slate-200 rounded-lg ingredient-rows-wrapper">
                                <table className="w-full">
                                    <thead className="hidden md:table-header-group">
                                        <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-medium">
                                            <th className="p-2 w-14"></th>
                                            <th className="p-2 text-left">Type</th>
                                            <th className="p-2 text-left">Produit / Sous-recette</th>
                                            <th className="p-2 text-right">Quantité</th>
                                            <th className="p-2 text-left">Unité</th>
                                            <th className="p-2 text-right">P.U.</th>
                                            <th className="p-2 text-right">Coût</th>
                                            <th className="p-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formData.ingredients.map((ing, i) => (
                                            <IngredientRow
                                                key={i}
                                                ingredient={ing}
                                                index={i}
                                                onUpdate={updateIngredient}
                                                onRemove={removeIngredient}
                                                onMoveUp={(idx) => moveIngredient(idx, idx - 1)}
                                                onMoveDown={(idx) => moveIngredient(idx, idx + 1)}
                                                isFirst={i === 0}
                                                isLast={i === formData.ingredients.length - 1}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Cost Summary */}
                    <div className="flex justify-end">
                        <div className="bg-slate-50 rounded-lg p-4 space-y-2 min-w-64">
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500">Coût total :</span>
                                <span className="font-semibold text-slate-900" data-testid="recipe-total-cost">
                                    {formatCurrency(totalCost)}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm border-t border-slate-200 pt-2">
                                <span className="text-slate-500">Coût / {pluralUnit(yieldLabel, 1)} ({formData.portions} {pluralUnit(yieldLabel, formData.portions)}) :</span>
                                <span className="font-bold text-lg text-slate-900" data-testid="recipe-cost-per-portion">
                                    {formatCurrency(costPerPortion)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="recipe-cancel-btn">
                        Annuler
                    </Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="recipe-save-btn">
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {recipe?.id ? 'Mettre à jour' : 'Créer la recette'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ExistingImagePicker({ open, onClose, onSelect, excludeId }) {
    const [recipes, setRecipes] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSearch('');
        setLoading(true);
        recipesAPI.getAll({ limit: 2000 })
            .then(r => setRecipes((r.data || []).filter(x => x.image_url && x.id !== excludeId)))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [open, excludeId]);

    const filtered = recipes.filter(r => !search || r.name?.toLowerCase().includes(search.toLowerCase()));

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="existing-image-picker">
                <DialogHeader>
                    <DialogTitle className="text-base">Choisir une photo existante</DialogTitle>
                    <DialogDescription>Sélectionnez la photo d'une autre recette — elle sera copiée sur cette recette.</DialogDescription>
                </DialogHeader>
                <Input placeholder="Rechercher une recette..." value={search} onChange={e => setSearch(e.target.value)} className="h-9" data-testid="existing-image-search" />
                {loading ? (
                    <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : filtered.length === 0 ? (
                    <p className="text-sm text-slate-500 py-6 text-center">Aucune recette avec photo.</p>
                ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                        {filtered.map(r => (
                            <button key={r.id} type="button" onClick={() => onSelect(r)}
                                className="group rounded-lg border border-slate-200 overflow-hidden hover:border-slate-900 hover:shadow-md transition-all text-left"
                                data-testid={`existing-image-${r.id}`}>
                                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${r.image_url}`} alt={r.name} loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                </div>
                                <div className="px-1.5 py-1 text-[10px] font-medium text-slate-700 truncate" title={r.name}>{r.name}</div>
                            </button>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
